import React, { cloneElement, Children, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useAccessibilityEscapeKey from './../hooks/useAccessibilityEscapeKey';
import { getAllParams } from './../service/utils';

const AccessibilityPopup = ({ close, children, role, ...params }) => {
    const popupRef = useRef(null);
    const lastFocusedElementRef = useRef(null);

    const setLastFocusedElement = () => {
        lastFocusedElementRef.current = document.activeElement;
    };

    const restoreFocus = () => {
        if (lastFocusedElementRef.current) {
            lastFocusedElementRef.current.focus();
        }
    };

    const getFocusableElements = () => {
        const elements = Array.from(
            popupRef.current.querySelectorAll(
                'a, button, input, textarea, select, iframe, [tabindex]:not([tabindex="-1"])'
            )
        );

        return elements;
    };

    const trapFocus = (e) => {
        const focusableElements = getFocusableElements();
        console.log('focusableElements', focusableElements)

        if (focusableElements.length === 0) {
            e.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    };

    useAccessibilityEscapeKey(() => {
        close();
        restoreFocus();
    });

    useEffect(() => {
        setLastFocusedElement();

        const focusableElements = getFocusableElements();

        if (focusableElements.length > 0) {
            focusableElements[0].focus();
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Tab') {
                trapFocus(e);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            restoreFocus();
        };
    }, []);

    const child = Children.only(children);

    return cloneElement(child, {
        role: role || 'dialog',
        ref: popupRef,
        'aria-modal': 'true',
        'aria-labelledby': params['aria-labelledby'] || 'dialog-title',
        'aria-describedby': params['aria-describedby'] || 'dialog-description',
        tabIndex: '-1',
        ...getAllParams(params),
    });
};

AccessibilityPopup.propTypes = {
    children: PropTypes.element.isRequired,
    close: PropTypes.func.isRequired,
};

export default AccessibilityPopup;
